import DOMPurify from 'dompurify';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { Assessment, UserAssessmentResult } from 'src/models/assessment/_types';
import { formatMonthDayYear } from 'src/utils/dateFormatter';

interface AssessmentCustomTemplateProps {
  assessment: Assessment;
  result?: boolean;
  results?: UserAssessmentResult[];
  offset?: number;
}

const generateProcessedHtml = (
  assessment: Assessment,
  results: UserAssessmentResult[] | undefined,
  offset: number,
  result: boolean,
  t: any
): { processedHtml: string | null; customCss: string | null } => {
  const template = result
    ? assessment.customResults || assessment.customTemplate
    : assessment.customTemplate;

  if (!template) return { processedHtml: null, customCss: null };

  try {
    let html = template;

    const css = assessment.customCss || null;

    if (result) {
      html = html.replace(
        /\{\{qv?(\d{1,3})\}\}/g,
        (match: string, origNumber: string) => {
          const resultIndex = offset
            ? parseInt(origNumber, 10) + offset - 1
            : parseInt(origNumber, 10) - 1;

          const resultValue = results?.[resultIndex];

          return renderResultValue(resultValue);
        }
      );
    } else {
      html = html.replace(
        /\{\{q(\d{1,3})\}\}/g,
        (match: string, origNumber: string) => {
          const fieldIndex = parseInt(origNumber, 10) - 1;
          return `<div data-field-index="${fieldIndex}" class="assessment-field-placeholder"></div>`;
        }
      );
    }

    return { processedHtml: html, customCss: css };
  } catch (error) {
    return {
      processedHtml: `<p style="color: red;">${t('templateError')}</p>`,
      customCss: null,
    };
  }
};

export const AssessmentCustomTemplate: React.FC<
  AssessmentCustomTemplateProps
> = ({ assessment, result = false, results, offset = 0 }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'assessmentResult',
  });

  const { processedHtml, customCss } = generateProcessedHtml(
    assessment,
    results,
    offset,
    result,
    t
  );

  if (!processedHtml) return null;

  return (
    <Box className="custom-assessment" sx={{ my: 2 }}>
      {customCss && (
        <style
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(customCss, {
              ALLOWED_TAGS: [],
              ALLOWED_ATTR: [],
              KEEP_CONTENT: true,
            }),
          }}
        />
      )}

      <HtmlTypography>{processedHtml}</HtmlTypography>
    </Box>
  );
};

function renderResultValue(result?: UserAssessmentResult): string {
  if (!result || (result.value === undefined && result.value !== 0)) {
    return '';
  }

  const htmlEncode = (str: string | number): string => {
    const div = document.createElement('div');
    div.textContent = String(str);
    return div.innerHTML;
  };

  switch (result.type) {
    case 'dob': {
      if (
        typeof result.value === 'string' ||
        typeof result.value === 'number'
      ) {
        return formatMonthDayYear(result.value, 'MMMM d, yyyy');
      }
      return htmlEncode(String(result.value));
    }

    case 'upload': {
      if (typeof result.value === 'object' && result.value !== null) {
        const uploadValue = result.value as {
          embed_url?: string;
          thumb_url?: string;
        };

        if (uploadValue.embed_url) {
          return `<iframe width="315" height="420" src="${htmlEncode(
            uploadValue.embed_url
          )}" frameborder="0" allowfullscreen></iframe>`;
        }
        if (uploadValue.thumb_url) {
          return `<img src="${htmlEncode(
            uploadValue.thumb_url
          )}" style="padding-bottom: 30px;" alt="Upload" />`;
        }
      }
      return '';
    }

    case 'rep_max': {
      if (typeof result.value === 'object' && result.value !== null) {
        const repMaxValue = result.value as { reps?: number; weight?: number };
        return `Reps: ${repMaxValue.reps || 0} | Weight: ${
          repMaxValue.weight || 0
        }`;
      }
      return '';
    }

    case 'password': {
      if (result.value) {
        return '••••••••';
      }
      return '';
    }

    case 'checkbox': {
      if (Array.isArray(result.value)) {
        return htmlEncode(result.value.join(', '));
      }
      return htmlEncode(String(result.value));
    }

    default: {
      if (typeof result.value === 'object') {
        return htmlEncode(JSON.stringify(result.value));
      }
      return htmlEncode(String(result.value));
    }
  }
}

export default AssessmentCustomTemplate;
